import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import Spinner from '../../../components/common/spinner';
import { Paginator } from '../../../components/common/paginator';
import VerticalMultiSelect from '../../../components/content-library/vertical-multi-select';
import TagMultiSelect from '../../../components/content-library/tag-multi-select';
import { ClientMultiSelectWithSearch } from '../../../components/content-library/filter-controls/ClientMultiSelectWithSearch';
import { use2026TagGroups } from '../../../hooks/useTagGroups';
import type { CollectionItem, ContentCollection } from '../../../hooks/useContentCollections';
import type { Vertical } from '../../../hooks/useClients';
import { useDomains } from '../../../hooks/useClients';
import { EmptyState } from './common';
import {
  formatCollectionItemType,
  formatDateTime,
  formatCollectionRelationship,
  getCollectionClientIds,
  getCollectionItemSummary,
} from '../utils/collection-helpers';

export interface CollectionsContentProps {
  collections: ContentCollection[];
  isLoadingList: boolean;
  count: number;
  currentPage: number;
  limit: number;
  onPageChange: (page: number) => void;
  onSelectCollection: (collectionId: number) => void;
  selectedCollectionId: number | null;
  selectedCollection?: ContentCollection;
  isLoadingCollection: boolean;
  onBack: () => void;
  onOpenAddItem: () => void;
  onRemoveItem: (itemId: number) => void;
  onShowItemDetails: (item: CollectionItem) => void;
  isUpdatingCollection: boolean;
  onToggleVisibility: (nextVisibility: boolean) => void;
  onTogglePublicEmbed: (nextIsPublic: boolean) => void;
  availableVerticals: Vertical[];
  isLoadingVerticals: boolean;
  onUpdateVerticals: (verticalIds: number[]) => void;
  onUpdateTags: (tagIds: number[]) => void;
  onUpdateDomain: (domainId: number | null) => void;
  onUpdateClient: (clientIds: number[]) => void;
  onReorderItems: (reorderedItems: CollectionItem[]) => void;
  onUpdateDescription: (description: string | null) => void;
  onUpdateName: (name: string) => void;
  onDeleteCollection: () => void;
  onDuplicateCollection: (collection: ContentCollection) => void;
  canReorderCollections?: boolean;
  onReorderCollections?: (reorderedCollections: ContentCollection[]) => void;
}

export const CollectionsContent: React.FC<CollectionsContentProps> = ({
  collections,
  isLoadingList,
  count,
  currentPage,
  limit,
  onPageChange,
  onSelectCollection,
  selectedCollectionId,
  selectedCollection,
  isLoadingCollection,
  onBack,
  onOpenAddItem,
  onRemoveItem,
  onShowItemDetails,
  isUpdatingCollection,
  onToggleVisibility,
  onTogglePublicEmbed,
  availableVerticals,
  isLoadingVerticals,
  onUpdateVerticals,
  onUpdateTags,
  onUpdateDomain,
  onUpdateClient,
  onReorderItems,
  onUpdateDescription,
  onUpdateName,
  onDeleteCollection,
  onDuplicateCollection,
  canReorderCollections = false,
  onReorderCollections,
}) => {
  if (selectedCollectionId) {
    if (isLoadingCollection || !selectedCollection) {
      return (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      );
    }

    return (
      <CollectionDetail
        collection={selectedCollection}
        onBack={onBack}
        onOpenAddItem={onOpenAddItem}
        onRemoveItem={onRemoveItem}
        onShowItemDetails={onShowItemDetails}
        isUpdating={isUpdatingCollection}
        onToggleVisibility={onToggleVisibility}
        onTogglePublicEmbed={onTogglePublicEmbed}
        availableVerticals={availableVerticals}
        isLoadingVerticals={isLoadingVerticals}
        onUpdateVerticals={onUpdateVerticals}
        onUpdateTags={onUpdateTags}
        onUpdateDomain={onUpdateDomain}
        onUpdateClient={onUpdateClient}
        onReorderItems={onReorderItems}
        onUpdateDescription={onUpdateDescription}
        onUpdateName={onUpdateName}
        onDeleteCollection={onDeleteCollection}
        onDuplicateCollection={onDuplicateCollection}
      />
    );
  }

  if (isLoadingList) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  if (!collections.length) {
    return <EmptyState message="No collections found." />;
  }

  return (
    <div>
      {!canReorderCollections && (
        <p className="mb-3 text-sm text-gray-500">
          Filter by a single client and clear search and other filters to drag and drop collections into display order
          for the member home page.
        </p>
      )}
      <CollectionsTable
        rows={collections}
        count={count}
        currentPage={currentPage}
        limit={limit}
        onPageChange={onPageChange}
        onSelectCollection={onSelectCollection}
        onDuplicateCollection={onDuplicateCollection}
        canReorder={canReorderCollections}
        isReordering={isUpdatingCollection}
        onReorder={onReorderCollections}
      />
    </div>
  );
};

interface CollectionsTableProps {
  rows: ContentCollection[];
  count: number;
  currentPage: number;
  limit: number;
  onPageChange: (page: number) => void;
  onSelectCollection: (collectionId: number) => void;
  onDuplicateCollection: (collection: ContentCollection) => void;
  canReorder?: boolean;
  isReordering?: boolean;
  onReorder?: (reorderedCollections: ContentCollection[]) => void;
}

const CollectionsTable: React.FC<CollectionsTableProps> = ({
  rows,
  count,
  currentPage,
  limit,
  onPageChange,
  onSelectCollection,
  onDuplicateCollection,
  canReorder = false,
  isReordering = false,
  onReorder,
}) => {
  const handleDragEnd = (result: DropResult) => {
    if (!canReorder || !onReorder || !result.destination) return;
    if (result.destination.index === result.source.index) return;

    const next = Array.from(rows);
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    onReorder(next.map((collection, index) => ({ ...collection, sort_order: index })));
  };

  const tableHead = (
    <tr className="bg-gray-100 text-left text-xs uppercase text-gray-500">
      {canReorder && <th className="w-12 px-4 py-4"></th>}
      {canReorder && <th className="w-16 px-4 py-4">Sort</th>}
      <th className="w-48 px-4 py-4">Name</th>
      <th className="w-64 px-4 py-4">Description</th>
      <th className="w-48 px-4 py-4">Relationship</th>
      <th className="w-20 px-4 py-4">Items</th>
      <th className="w-24 px-4 py-4">Visibility</th>
      <th className="w-32 px-4 py-4">Created</th>
      <th className="w-40 px-4 py-4 text-right">Actions</th>
    </tr>
  );

  const renderRowCells = (collection: ContentCollection) => (
    <>
      <td className="px-4 py-4 align-top">
        <div className="min-w-0 truncate font-semibold" title={collection.name}>
          {collection.name}
        </div>
      </td>
      <td className="px-4 py-4 align-top">
        <div className="min-w-0 truncate text-sm text-gray-600" title={collection.description || undefined}>
          {collection.description || '—'}
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-gray-600">{formatCollectionRelationship(collection)}</td>
      <td className="px-4 py-4 font-semibold">{collection.items.length}</td>
      <td className="px-4 py-4 capitalize">{collection.visibility ? 'Visible' : 'Hidden'}</td>
      <td className="px-4 py-4 text-sm text-gray-500">{formatDateTime(collection.created_at)}</td>
      <td className="px-4 py-4">
        <div className="flex justify-end gap-2">
          <button
            className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700"
            onClick={() => onSelectCollection(collection.id)}
          >
            View
          </button>
          <button
            className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700"
            onClick={() => onDuplicateCollection(collection)}
          >
            Duplicate
          </button>
        </div>
      </td>
    </>
  );

  return (
    <div>
      <div className="overflow-x-auto">
        {canReorder ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <table className="w-full table-fixed border-collapse">
              <thead>{tableHead}</thead>
              <Droppable droppableId="client-collections">
                {(provided, snapshot) => (
                  <tbody
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={snapshot.isDraggingOver ? 'bg-gray-50' : ''}
                  >
                    {rows.map((collection, index) => (
                      <Draggable
                        key={collection.id}
                        draggableId={collection.id.toString()}
                        index={index}
                        isDragDisabled={isReordering}
                      >
                        {(dragProvided, dragSnapshot) => (
                          <tr
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            className={`border-b border-gray-100 text-sm text-gray-700 ${
                              dragSnapshot.isDragging ? 'bg-white shadow-md' : ''
                            }`}
                          >
                            <td
                              {...dragProvided.dragHandleProps}
                              className="cursor-grab px-4 py-4 active:cursor-grabbing"
                            >
                              <svg
                                className="h-5 w-5 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 8h16M4 16h16"
                                />
                              </svg>
                            </td>
                            <td className="px-4 py-4">{(collection.sort_order ?? index) + 1}</td>
                            {renderRowCells(collection)}
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </table>
          </DragDropContext>
        ) : (
          <table className="w-full table-fixed border-collapse">
            <thead>{tableHead}</thead>
            <tbody>
              {rows.map((collection) => (
                <tr key={collection.id} className="border-b border-gray-100 text-sm text-gray-700">
                  {renderRowCells(collection)}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="mt-4 flex justify-end">
        <Paginator count={count} currentPage={currentPage} limit={limit} handlePageChange={onPageChange} />
      </div>
    </div>
  );
};

interface CollectionDetailProps {
  collection: ContentCollection;
  onBack: () => void;
  onOpenAddItem: () => void;
  onRemoveItem: (itemId: number) => void;
  onShowItemDetails: (item: CollectionItem) => void;
  isUpdating: boolean;
  onToggleVisibility: (nextVisibility: boolean) => void;
  onTogglePublicEmbed: (nextIsPublic: boolean) => void;
  availableVerticals: Vertical[];
  isLoadingVerticals: boolean;
  onUpdateVerticals: (verticalIds: number[]) => void;
  onUpdateTags: (tagIds: number[]) => void;
  onUpdateDomain: (domainId: number | null) => void;
  onUpdateClient: (clientIds: number[]) => void;
  onReorderItems: (reorderedItems: CollectionItem[]) => void;
  onUpdateDescription: (description: string | null) => void;
  onUpdateName: (name: string) => void;
  onDeleteCollection: () => void;
  onDuplicateCollection: (collection: ContentCollection) => void;
}

const CollectionDetail: React.FC<CollectionDetailProps> = ({
  collection,
  onBack,
  onOpenAddItem,
  onRemoveItem,
  onShowItemDetails,
  isUpdating,
  onToggleVisibility,
  onTogglePublicEmbed,
  availableVerticals,
  isLoadingVerticals,
  onUpdateVerticals,
  onUpdateTags,
  onUpdateDomain,
  onUpdateClient,
  onReorderItems,
  onUpdateDescription,
  onUpdateName,
  onDeleteCollection,
  onDuplicateCollection,
}) => {
  const { allTags, isLoading: isLoadingTags } = use2026TagGroups();
  const { data: domains } = useDomains();
  const assignedClientIds = useMemo(() => getCollectionClientIds(collection), [collection]);
  const assignedClients = useMemo(
    () =>
      (collection.clients || []).map((c) => ({
        id: c.id,
        name: c.name || `Client #${c.id}`,
      })),
    [collection.clients],
  );
  const items = useMemo(() => {
    return [...collection.items].sort((a, b) => a.sort_order - b.sort_order);
  }, [collection.items]);

  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(collection.name);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(collection.description || '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    setEditedName(collection.name);
    setIsEditingName(false);
  }, [collection.name]);

  useEffect(() => {
    setEditedDescription(collection.description || '');
    setIsEditingDescription(false);
  }, [collection.description]);

  const handleSaveName = useCallback(() => {
    const trimmedName = editedName.trim();
    if (trimmedName) {
      onUpdateName(trimmedName);
      setIsEditingName(false);
    }
  }, [editedName, onUpdateName]);

  const handleCancelName = useCallback(() => {
    setEditedName(collection.name);
    setIsEditingName(false);
  }, [collection.name]);

  const handleSaveDescription = useCallback(() => {
    const trimmedDescription = editedDescription.trim();
    onUpdateDescription(trimmedDescription || null);
    setIsEditingDescription(false);
  }, [editedDescription, onUpdateDescription]);

  const handleCancelDescription = useCallback(() => {
    setEditedDescription(collection.description || '');
    setIsEditingDescription(false);
  }, [collection.description]);

  const handleConfirmDelete = useCallback(() => {
    onDeleteCollection();
    setShowDeleteConfirm(false);
  }, [onDeleteCollection]);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }

      const sourceIndex = result.source.index;
      const destinationIndex = result.destination.index;

      if (sourceIndex === destinationIndex) {
        return;
      }

      const reorderedItems = Array.from(items);
      const [removed] = reorderedItems.splice(sourceIndex, 1);
      reorderedItems.splice(destinationIndex, 0, removed);

      const reorderedItemsWithUpdatedSortOrder = reorderedItems.map((item, index) => ({
        ...item,
        sort_order: index,
      }));

      onReorderItems(reorderedItemsWithUpdatedSortOrder);
    },
    [items, onReorderItems],
  );

  const [selectedVerticalIds, setSelectedVerticalIds] = useState<number[]>(() =>
    collection.verticals.map((vertical) => vertical.id),
  );
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(() => collection.tags.map((tag) => tag.id));

  useEffect(() => {
    setSelectedVerticalIds(collection.verticals.map((vertical) => vertical.id));
  }, [collection.verticals]);

  useEffect(() => {
    setSelectedTagIds(collection.tags.map((tag) => tag.id));
  }, [collection.tags]);

  const handleVerticalChange = useCallback(
    (nextIds: number[]) => {
      setSelectedVerticalIds(nextIds);
      onUpdateVerticals(nextIds);
    },
    [onUpdateVerticals],
  );

  const handleTagChange = useCallback(
    (nextIds: number[]) => {
      setSelectedTagIds(nextIds);
      onUpdateTags(nextIds);
    },
    [onUpdateTags],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-4">
          <button className="text-blue-primary text-sm font-semibold" onClick={onBack}>
            {'< Back to Collections'}
          </button>
          <div className="mt-2">
            {isEditingName ? (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-2xl font-semibold text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder="Collection name"
                  disabled={isUpdating}
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    className="bg-blue-primary rounded-md px-3 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={handleSaveName}
                    disabled={isUpdating || !editedName.trim()}
                  >
                    Save
                  </button>
                  <button
                    className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={handleCancelName}
                    disabled={isUpdating}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="group flex items-center gap-2">
                <h2 className="text-2xl font-semibold text-gray-900">{collection.name}</h2>
                <button
                  className="text-blue-primary text-xs font-semibold"
                  onClick={() => setIsEditingName(true)}
                  disabled={isUpdating}
                >
                  Edit
                </button>
              </div>
            )}
          </div>
          <div className="max-w-2xl">
            {isEditingDescription ? (
              <div className="space-y-2">
                <textarea
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  rows={3}
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  placeholder="Add a description..."
                  disabled={isUpdating}
                />
                <div className="flex gap-2">
                  <button
                    className="bg-blue-primary rounded-md px-3 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={handleSaveDescription}
                    disabled={isUpdating}
                  >
                    Save
                  </button>
                  <button
                    className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={handleCancelDescription}
                    disabled={isUpdating}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="group flex items-start gap-2">
                <p className="text-sm text-gray-600">{collection.description || 'No description'}</p>
                <button
                  className="text-blue-primary text-xs font-semibold"
                  onClick={() => setIsEditingDescription(true)}
                  disabled={isUpdating}
                >
                  Edit
                </button>
              </div>
            )}
          </div>
          <div className="mt-4 max-w-sm">
            <ClientMultiSelectWithSearch
              label="Clients (Groups)"
              selectedClientIds={assignedClientIds}
              selectedClients={assignedClients}
              onChange={onUpdateClient}
              placeholder="None (not client-scoped)"
              disabled={isUpdating}
            />
            <p className="mt-1 text-xs text-gray-500">
              Clients are mutually exclusive with Domain and Verticals. Enabling one or more clients clears Domain and
              Verticals. Domain and Verticals can still be used together.
            </p>
          </div>
          <div className="mt-4 max-w-sm">
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Domain</label>
            <select
              className="focus:ring-blue-primary mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
              value={collection.domain_id ?? ''}
              onChange={(e) => onUpdateDomain(e.target.value ? parseInt(e.target.value, 10) : null)}
              disabled={isUpdating}
            >
              <option value="">None (global)</option>
              {domains?.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} (#{d.id})
                </option>
              ))}
            </select>
          </div>
          <div className="mt-4 max-w-sm">
            <VerticalMultiSelect
              label="Verticals"
              verticals={availableVerticals}
              selectedVerticalIds={selectedVerticalIds}
              onChange={handleVerticalChange}
              disabled={isUpdating}
              isLoading={isLoadingVerticals}
            />
          </div>
          <div className="mt-4 max-w-sm">
            <TagMultiSelect
              label="Tags"
              tags={allTags}
              selectedTagIds={selectedTagIds}
              onChange={handleTagChange}
              disabled={isUpdating}
              isLoading={isLoadingTags}
              placeholder="Select tags (optional)"
            />
          </div>
          <div className="mt-4 flex flex-row items-center gap-2">
            <span className="text-xs uppercase text-gray-500">Can members see this?</span>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <input
                type="checkbox"
                className="text-blue-primary focus:ring-blue-primary h-4 w-4 rounded border-gray-300"
                checked={collection.visibility}
                onChange={(event) => onToggleVisibility(event.target.checked)}
                disabled={isUpdating}
              />
              {collection.visibility ? 'Yes' : 'No'}
            </label>
          </div>
          <div className="mt-4 max-w-xl space-y-2">
            <div className="flex flex-row items-center gap-2">
              <span className="text-xs uppercase text-gray-500">Public embed link</span>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <input
                  type="checkbox"
                  className="text-blue-primary focus:ring-blue-primary h-4 w-4 rounded border-gray-300"
                  checked={!!collection.is_public}
                  onChange={(event) => onTogglePublicEmbed(event.target.checked)}
                  disabled={isUpdating || (assignedClientIds.length === 0 && !collection.is_public)}
                />
                {collection.is_public ? 'Enabled' : 'Disabled'}
              </label>
            </div>
            {assignedClientIds.length === 0 && (
              <p className="text-xs text-gray-500">Assign at least one client before enabling a public embed link.</p>
            )}
            {collection.is_public && collection.public_key && assignedClients.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500">
                  One link per enabled client. The <code>client_id</code> query param sets group code for onboarding.
                </p>
                {assignedClients.map((client) => {
                  const url = `${process.env.REACT_APP_MWA_LINK || ''}/public/collections/${collection.public_key}?client_id=${client.id}`;
                  return (
                    <div key={client.id} className="flex flex-col gap-1">
                      <span className="text-xs font-semibold text-gray-600">
                        {client.name} (#{client.id})
                      </span>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <input
                          type="text"
                          readOnly
                          className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-xs text-gray-700"
                          value={url}
                        />
                        <button
                          type="button"
                          className="bg-blue-primary h-9 shrink-0 rounded-md px-3 text-xs font-semibold text-white"
                          onClick={() => {
                            void navigator.clipboard.writeText(url);
                          }}
                        >
                          Copy link
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="text-xs text-gray-500">
            Drag rows to reorder items. Removing an item updates the collection immediately.
          </div>
        </div>
        <div className="flex gap-2">
          <button
            className="bg-blue-primary h-10 rounded-md px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onOpenAddItem}
            disabled={isUpdating}
          >
            Add Item
          </button>
          <button
            className="h-10 rounded-md border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => onDuplicateCollection(collection)}
            disabled={isUpdating}
          >
            Duplicate
          </button>
          <button
            className="h-10 rounded-md border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isUpdating}
          >
            Delete Collection
          </button>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-red-800">Delete this collection?</p>
              <p className="text-sm text-red-600">
                This will permanently delete "{collection.name}" and remove all items from it. This action cannot be
                undone.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                className="rounded-md border border-red-600 bg-red-600 px-3 py-1 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                style={{ backgroundColor: '#dc2626' }}
                onClick={handleConfirmDelete}
                disabled={isUpdating}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200">
        {items.length ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="overflow-x-auto">
              <table className="w-full table-fixed border-collapse">
                <thead>
                  <tr className="bg-gray-100 text-left text-xs uppercase text-gray-500">
                    <th className="w-12 px-4 py-4"></th>
                    <th className="w-16 px-4 py-4">Sort</th>
                    <th className="w-28 px-4 py-4">Type</th>
                    <th className="w-64 px-4 py-4">Item</th>
                    <th className="w-32 px-4 py-4">Added</th>
                    <th className="w-40 px-4 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <Droppable droppableId="collection-items">
                  {(provided, snapshot) => (
                    <tbody
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className={snapshot.isDraggingOver ? 'bg-gray-50' : ''}
                    >
                      {items.map((item, index) => {
                        const summary = getCollectionItemSummary(item);
                        return (
                          <Draggable
                            key={item.id}
                            draggableId={item.id.toString()}
                            index={index}
                            isDragDisabled={isUpdating}
                          >
                            {(provided, snapshot) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`border-b border-gray-100 text-sm text-gray-700 ${
                                  snapshot.isDragging ? 'bg-white shadow-md' : ''
                                }`}
                              >
                                <td
                                  {...provided.dragHandleProps}
                                  className="cursor-grab px-4 py-4 active:cursor-grabbing"
                                >
                                  <svg
                                    className="h-5 w-5 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 8h16M4 16h16"
                                    />
                                  </svg>
                                </td>
                                <td className="px-4 py-4">{item.sort_order + 1}</td>
                                <td className="px-4 py-4 font-semibold">
                                  {formatCollectionItemType(item.contentType)}
                                </td>
                                <td className="px-4 py-4 align-top">
                                  <div className="min-w-0 truncate font-semibold" title={summary.title}>
                                    {summary.title}
                                  </div>
                                  {summary.subtitle && (
                                    <div
                                      className="min-w-0 truncate text-xs text-gray-500"
                                      title={summary.subtitle.replace(/<[^>]*>/g, '')}
                                      dangerouslySetInnerHTML={{ __html: summary.subtitle }}
                                    />
                                  )}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-500">{formatDateTime(item.created_at)}</td>
                                <td className="px-4 py-4">
                                  <div className="flex justify-end gap-2">
                                    <button
                                      className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700"
                                      onClick={() => onShowItemDetails(item)}
                                    >
                                      View Details
                                    </button>
                                    <button
                                      className="rounded-md border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                                      onClick={() => onRemoveItem(item.id)}
                                      disabled={isUpdating}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </table>
            </div>
          </DragDropContext>
        ) : (
          <div className="p-8">
            <EmptyState message="No items in this collection yet." />
          </div>
        )}
      </div>
    </div>
  );
};
